const chapter = {
  id: 'solutions',
  slug: 'solutions',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Solutions',
        hindi: 'विलयन',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'concentration',
      title: {
        english: 'Expressing Concentration',
        hindi: 'सान्द्रता व्यक्त करना',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Term', 'Symbol', 'Definition', 'Unit'],
          rows: [
            ['Mass percent', '$w/w \\%$', '(mass solute / mass solution)·100', '%'],
            ['Volume percent', '$v/v \\%$', '(vol solute / vol solution)·100', '%'],
            ['Mass / volume %', '$w/v \\%$', '(mass solute / vol solution)·100', 'g/100 mL'],
            ['Parts per million', 'ppm', '(mass solute / mass solution)·$10^6$', '—'],
            ['Mole fraction', '$x_i$', '$n_i / \\sum n_j$', 'dimensionless'],
            ['Molarity', '$M$', 'mol solute / L solution', 'mol/L'],
            ['Molality', '$m$', 'mol solute / kg **solvent**', 'mol/kg'],
            ['Normality', '$N$', '$g$-eq solute / L solution', 'eq/L'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Molality and mole fraction are independent of temperature; molarity is not (volume changes with $T$).',
        hindi: 'मोलैलिटी और मोल अंश तापमान से स्वतंत्र होते हैं; मोलरिटी नहीं है (आयतन $T$ के साथ बदलता है)।',
          },
        },
      ],
    },
    {
      id: 'solubility',
      title: {
        english: 'Solubility & Henry\'s Law',
        hindi: 'घुलनशीलता और हेनरी का नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Solid in liquid: solubility usually $\\uparrow$ with $T$ if dissolution endothermic; $\\downarrow$ if exothermic (Le Chatelier).',
        hindi: 'तरल में ठोस: घुलनशीलता आमतौर पर $\\uparrow$ के साथ $T$ यदि विघटन एंडोथर्मिक है; $\\downarrow$ यदि ऊष्माक्षेपी (ले चेटेलियर)।',
            },
            {
              english: 'Gas in liquid: solubility $\\downarrow$ with $T$ ($\\Delta_{sol} H < 0$); $\\uparrow$ with $P$.',
        hindi: 'तरल में गैस: घुलनशीलता $\\downarrow$ $T$ ($\\Delta_{sol} H < 0$) के साथ; $\\uparrow$ $P$ के साथ।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Henry\'s law',
        hindi: 'हेनरी का नियम',
          },
          statement: {
            english: 'At constant $T$, the partial pressure of a gas above a solution is proportional to its mole fraction in solution: $$p_{\\text{gas}} = K_H \\, x_{\\text{gas}}.$$ Higher $K_H$ ⇒ lower solubility. Applications: aerated drinks (high $P$ to dissolve CO₂), scuba diving (decompression sickness), high-altitude anoxia.',
        hindi: 'स्थिरांक $T$ पर, घोल के ऊपर गैस का आंशिक दबाव घोल में उसके मोल अंश के समानुपाती होता है: $$p_{\\text{gas}} = K_H \\, x_{\\text{gas}}.$$ उच्चतर $K_H$ ⇒ कम घुलनशीलता। अनुप्रयोग: वातित पेय (CO₂ को घोलने के लिए उच्च $P$), स्कूबा डाइविंग (डीकंप्रेसन बीमारी), उच्च ऊंचाई वाले एनोक्सिया।',
          },
        },
      ],
    },
    {
      id: 'raoult',
      title: {
        english: 'Vapour Pressure & Raoult\'s Law',
        hindi: 'वाष्प दबाव और राउल्ट का नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Raoult\'s law (volatile + volatile)',
        hindi: 'राउल्ट का नियम (अस्थिर + अस्थिर)',
          },
          statement: {
            english: `For an ideal solution of two volatile liquids:
$p_A = p_A^\\circ x_A,\\ p_B = p_B^\\circ x_B$ and total $p_T = p_A^\\circ x_A + p_B^\\circ x_B$. Mole fraction of $A$ in vapour: $y_A = p_A/p_T$.`,
            hindi: 'दो अस्थिर तरल पदार्थों के आदर्श समाधान के लिए: $p_A = p_A^\\circ x_A,\\\\\\\\ p_B = p_B^\\circ x_B$ और कुल $p_T = p_A^\\circ x_A + p_B^\\circ x_B$। वाष्प में $A$ का मोल अंश: $y_A = p_A/p_T$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Raoult\'s law (non-volatile solute)',
        hindi: 'राउल्ट का नियम (गैर-वाष्पशील विलेय)',
          },
          statement: {
            english: '$p_{\\text{soln}} = p_A^\\circ x_A$. Relative lowering of vapour pressure $\\dfrac{p_A^\\circ - p_{\\text{soln}}}{p_A^\\circ} = x_B$ (mole fraction of solute) — a colligative property.',
        hindi: '$p_{\\text{soln}} = p_A^\\circ x_A$. वाष्प दबाव की सापेक्ष कमी $\\dfrac{p_A^\\circ - p_{\\text{soln}}}{p_A^\\circ} = x_B$ (विलेय का मोल अंश) - एक सहसंयोजक गुण।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Ideal solutions**: obey Raoult\'s law over entire composition; $\\Delta_{\\text{mix}} H = 0$, $\\Delta_{\\text{mix}} V = 0$. Examples: benzene + toluene, n-hexane + n-heptane.',
        hindi: '**आदर्श समाधान**: संपूर्ण रचना पर राउल्ट के नियम का पालन करें; $\\Delta_{\\text{mix}} H = 0$, $\\Delta_{\\text{mix}} V = 0$. उदाहरण: बेंजीन + टोल्यूनि, एन-हेक्सेन + एन-हेप्टेन।',
            },
            {
              english: '**Positive deviation** ($p_T > $ predicted): A–B interactions weaker than A–A & B–B; $\\Delta H_{\\text{mix}} > 0$, $\\Delta V > 0$. Forms **minimum-boiling azeotrope**. Examples: ethanol + water, acetone + CS₂.',
        hindi: '**सकारात्मक विचलन** ($p_T > $ अनुमानित): ए-बी इंटरैक्शन ए-ए और बी-बी से कमजोर; $\\Delta H_{\\text{mix}} > 0$, $\\Delta V > 0$. **न्यूनतम-उबलने वाला एज़ोट्रोप** बनता है। उदाहरण: इथेनॉल + पानी, एसीटोन + सीएस₂।',
            },
            {
              english: '**Negative deviation** ($p_T <$ predicted): A–B stronger (e.g. H-bonding); $\\Delta H < 0$, $\\Delta V < 0$. Forms **maximum-boiling azeotrope**. Examples: HCl + water (azeotrope at ~20.2 % HCl, 110 °C), HNO₃ + water, chloroform + acetone.',
        hindi: '**नकारात्मक विचलन** ($p_T <$ अनुमानित): ए-बी मजबूत (जैसे एच-बॉन्डिंग); $\\Delta H < 0$, $\\Delta V < 0$. **अधिकतम-उबलने वाला एज़ोट्रोप** बनता है। उदाहरण: एचसीएल + पानी (एज़ोट्रोप ~20.2% एचसीएल, 110 डिग्री सेल्सियस पर), एचएनओ₃ + पानी, क्लोरोफॉर्म + एसीटोन।',
            },
            {
              english: '**Azeotrope** = constant-boiling mixture which distills without change in composition; cannot be separated by simple distillation.',
        hindi: '**एज़ियोट्रोप** = लगातार उबलने वाला मिश्रण जो संरचना में बदलाव के बिना आसवित होता है; साधारण आसवन द्वारा अलग नहीं किया जा सकता।',
            },
          ],
        },
      ],
    },
    {
      id: 'colligative',
      title: {
        english: 'Colligative Properties',
        hindi: 'अणुसंख्य गुणधर्म',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Properties that depend on the **number** of solute particles, not their identity. Four classical colligative properties:',
        hindi: 'गुण जो विलेय कणों की **संख्या** पर निर्भर करते हैं, न कि उनकी पहचान पर। चार शास्त्रीय सहसंयोजक गुण:',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Relative lowering of vapour pressure**: $\\dfrac{\\Delta p}{p_A^\\circ} = x_B$ (Raoult).',
        hindi: '**वाष्प दबाव का सापेक्षिक रूप से कम होना**: $\\dfrac{\\Delta p}{p_A^\\circ} = x_B$ (राउल्ट)।',
            },
            {
              english: '**Elevation of boiling point**: $\\Delta T_b = K_b \\, m$ (where $K_b$ = molal ebullioscopic constant). For water $K_b = 0.52$ K·kg/mol.',
        hindi: '**क्वथनांक का उन्नयन**: $\\Delta T_b = K_b \\, m$ (जहाँ $K_b$ = मोलल एबुलियोस्कोपिक स्थिरांक)। पानी के लिए $K_b = 0.52$ K·kg/mol.',
            },
            {
              english: '**Depression of freezing point**: $\\Delta T_f = K_f \\, m$. For water $K_f = 1.86$ K·kg/mol.',
        hindi: '**हिमांक बिंदु का अवनमन**: $\\Delta T_f = K_f \\, m$. पानी के लिए $K_f = 1.86$ K·kg/mol.',
            },
            {
              english: '**Osmotic pressure**: $\\pi = C R T = \\dfrac{n_B}{V}RT$ (for dilute solutions). Solutions of equal $\\pi$ are **isotonic**.',
        hindi: '**आसमाटिक दबाव**: $\\pi = C R T = \\dfrac{n_B}{V}RT$ (पतला समाधान के लिए)। समान $\\pi$ के समाधान **आइसोटोनिक** हैं।',
            },
            {
              english: 'From any of these, molar mass of solute can be calculated: e.g. $M_B = \\dfrac{w_B R T}{\\pi V}$.',
        hindi: 'इनमें से किसी से भी, विलेय के दाढ़ द्रव्यमान की गणना की जा सकती है: उदाहरण के लिए। $M_B = \\dfrac{w_B R T}{\\pi V}$.',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'van\'t Hoff factor $i$',
        hindi: 'वान्ट हॉफ फ़ैक्टर $i$',
          },
          body: {
            english: `$i = \\dfrac{\\text{actual moles of particles}}{\\text{moles dissolved}} = \\dfrac{\\text{observed colligative}}{\\text{calculated colligative}}$.
• Non-electrolyte: $i = 1$. 
• Dissociating solute (NaCl → 2 ions): $i \\to 2$. 
• Associating solute (acetic acid in benzene dimerises): $i < 1$ ($\\to 0.5$). 
Degree of dissociation: $\\alpha = (i-1)/(n-1)$. Degree of association: $\\alpha = (1-i)\\,n/(n-1)$.
Modified colligative: multiply by $i$ (e.g. $\\Delta T_f = i K_f m$, $\\pi = i C R T$).`,
            hindi: '$i = \\dfrac{\\text{actual moles of particles}}{\\text{moles dissolved}} = \\dfrac{\\text{observed colligative}}{\\text{calculated colligative}}$. • गैर-इलेक्ट्रोलाइट: $i = 1$. • पृथक्कारी विलेय (NaCl → 2 आयन): $i \\to 2$। • सहयोगी विलेय (बेंजीन डिमेरिस में एसिटिक एसिड): $i < 1$ ($\\to 0.5$)। पृथक्करण की डिग्री: $\\alpha = (i-1)/(n-1)$. एसोसिएशन की डिग्री: $\\alpha = (1-i)\\,n/(n-1)$. संशोधित कोलिगेटिव: $i$ से गुणा करें (जैसे $\\Delta T_f = i K_f m$, $\\pi = i C R T$)।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find freezing point of a solution containing 2.0 g KCl in 100 g water. ($K_f$ for water = 1.86, assume complete dissociation.)',
        hindi: '100 ग्राम पानी में 2.0 ग्राम KCl वाले घोल का हिमांक ज्ञात कीजिए। ($K_f$ के लिए water = 1.86, पूर्ण पृथक्करण मान लें।)',
          },
          solution: {
            english: `Molality $m = (2.0/74.5)/0.100 = 0.268$. $i = 2$ (KCl).
$\\Delta T_f = 2 \\times 1.86 \\times 0.268 \\approx 1.0$ K.
$T_f = -1.0$ °C.`,
            hindi: 'मोलैलिटी $m = (2.0/74.5)/0.100 = 0.268$. $i = 2$ (KCl)। $\\Delta T_f = 2 \\times 1.86 \\times 0.268 \\approx 1.0$ के. $T_f = -1.0$ डिग्री सेल्सियस।',
          },
        },
      ],
    },
    {
      id: 'osmosis',
      title: {
        english: 'Osmosis & Reverse Osmosis',
        hindi: 'परासरण और विपरीत परासरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Osmosis**: spontaneous flow of solvent through a semi-permeable membrane from lower-solute to higher-solute side.',
        hindi: '**ऑस्मोसिस**: एक अर्ध-पारगम्य झिल्ली के माध्यम से निचले-विलेय से उच्च-घुलनशील पक्ष की ओर विलायक का सहज प्रवाह।',
            },
            {
              english: '**Osmotic pressure** $\\pi$: external pressure needed to stop osmosis. Most accurate colligative property for macromolecules (proteins, polymers) because measurable at small concentrations and at room $T$.',
        hindi: '**आसमाटिक दबाव** $\\pi$: परासरण को रोकने के लिए आवश्यक बाहरी दबाव। मैक्रोमोलेक्यूल्स (प्रोटीन, पॉलिमर) के लिए सबसे सटीक कोलिगेटिव प्रॉपर्टी क्योंकि छोटी सांद्रता और कमरे $T$ पर मापी जा सकती है।',
            },
            {
              english: '**Hypertonic** (higher $\\pi$ than reference): cells crenate. **Hypotonic**: cells swell / burst. **Isotonic** (same $\\pi$, e.g. 0.91 % NaCl ≡ blood plasma) — used as IV fluid.',
        hindi: '**हाइपरटोनिक** (संदर्भ से अधिक $\\pi$): कोशिकाएं क्रेनेट होती हैं। **हाइपोटोनिक**: कोशिकाएं सूज जाती हैं/फट जाती हैं। **आइसोटोनिक** (समान $\\pi$, उदाहरण के लिए 0.91 % NaCl ≡ रक्त प्लाज्मा) - IV द्रव के रूप में उपयोग किया जाता है।',
            },
            {
              english: '**Reverse osmosis**: apply $P > \\pi$ on solution → solvent flows opposite direction. Used in desalination of sea water (cellulose acetate / polyamide membranes).',
        hindi: '**रिवर्स ऑस्मोसिस**: घोल पर $P > \\pi$ लगाएं → विलायक विपरीत दिशा में प्रवाहित होता है। समुद्री जल (सेलूलोज़ एसीटेट/पॉलियामाइड झिल्ली) के अलवणीकरण में उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'p_A = p_A^\\circ x_A',
      desc: {
        english: 'Raoult — partial pressure',
        hindi: 'राउल्ट - आंशिक दबाव',
      },
    },
    {
      latex: 'p_{\\text{gas}} = K_H \\, x_{\\text{gas}}',
      desc: {
        english: 'Henry\'s law',
        hindi: 'हेनरी का नियम',
      },
    },
    {
      latex: '\\Delta T_b = i K_b m,\\ \\Delta T_f = i K_f m',
      desc: {
        english: 'Boiling/freezing colligative',
        hindi: 'Boiling/freezing सहयोगात्मक',
      },
    },
    {
      latex: '\\pi = i C R T',
      desc: {
        english: 'Osmotic pressure (van\'t Hoff)',
        hindi: 'आसमाटिक दबाव (वान्ट हॉफ)',
      },
    },
    {
      latex: 'i = 1 + (n-1)\\alpha',
      desc: {
        english: 'Dissociation',
        hindi: 'पृथक्करण',
      },
    },
    {
      latex: 'i = 1 - \\dfrac{n-1}{n}\\alpha',
      desc: {
        english: 'Association',
        hindi: 'एसोसिएशन',
      },
    },
    {
      latex: '\\dfrac{p_A^\\circ - p}{p_A^\\circ} = x_B',
      desc: {
        english: 'Relative lowering of v.p.',
        hindi: 'वी.पी. की सापेक्ष कमी।',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Henry',
      problem: {
        english: 'Solubility of $CO_2$ in soft drinks is governed by:',
        hindi: 'शीतल पेय में $CO_2$ की घुलनशीलता निम्न द्वारा नियंत्रित होती है:',
      },
      answer: {
        english: 'Henry\'s law',
        hindi: 'हेनरी का नियम',
      },
      solution: {
        english: '$p = K_H x$.',
        hindi: '$p = K_H x$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'van\'t Hoff',
      problem: {
        english: 'For 0.1 M $K_4[Fe(CN)_6]$ assumed completely dissociated, $i$?',
        hindi: '0.1 एम के लिए $K_4[Fe(CN)_6]$ को पूरी तरह से अलग माना गया, $i$?',
      },
      answer: {
        english: '5',
        hindi: '5',
      },
      solution: {
        english: 'Gives 4 K⁺ + 1 complex anion = 5 ions.',
        hindi: '4 K⁺ + 1 कॉम्प्लेक्स anion = 5 ions देता है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Azeotrope',
      problem: {
        english: 'Acetone + chloroform shows:',
        hindi: 'एसीटोन + क्लोरोफॉर्म दिखाता है:',
      },
      answer: {
        english: 'Negative deviation, max-boiling azeotrope',
        hindi: 'नकारात्मक विचलन, अधिकतम क्वथनांक वाला एज़ोट्रोप',
      },
      solution: {
        english: 'A–B H-bonding stronger than A–A, B–B.',
        hindi: 'ए-बी एच-बॉन्डिंग ए-ए, बी-बी से अधिक मजबूत है। पानी में NaCl, MgCl₂, AlCl₃ के समान मोलल घोल; निम्नतम हिमांक बिंदु से संबंधित है:',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Colligative',
      problem: {
        english: 'Equal molal solutions of NaCl, MgCl₂, AlCl₃ in water; lowest freezing point belongs to:',
        hindi: 'पानी में NaCl, MgCl₂, AlCl₃ के समान मोलल घोल; निम्नतम हिमांक बिंदु से संबंधित है:',
      },
      answer: {
        english: 'AlCl₃',
        hindi: 'AlCl₃',
      },
      solution: {
        english: 'Highest $i$ ($\\to 4$).',
        hindi: 'उच्चतम $i$ ($\\to 4$)।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Osmotic',
      problem: {
        english: 'Osmotic pressure of 0.1 M urea at 300 K? ($R = 0.0821$ L·atm/mol·K)',
        hindi: '300 K पर 0.1 M यूरिया का आसमाटिक दबाव? ($R = 0.0821$ L·atm/mol·K)',
      },
      answer: {
        english: '≈ 2.46 atm',
        hindi: '≈ 2.46 एटीएम',
      },
      solution: {
        english: '$\\pi = CRT = 0.1 \\times 0.0821 \\times 300$.',
        hindi: '$\\pi = CRT = 0.1 \\ गुना 0.0821 \\ गुना 300$।',
      },
    },
  ],
  next: {
    slug: 'electrochemistry',
    title: 'Electrochemistry',
  },
};

export default chapter;
